import { useSelector, useDispatch } from 'react-redux';

// Hook for reading/changing the site's current language anywhere.
// Usage: const { lang, setLang } = useLang();
export const useLang = () => {
    const lang = useSelector((state) => state.language.lang);
    const dispatch = useDispatch();
    const setLang = (newLang) => dispatch({ type: 'SET_LANGUAGE', payload: newLang });
    return { lang, setLang };
};

// Picks the Chinese version of a DB field when available and the site is
// in Chinese, otherwise falls back to English — so a record that hasn't
// been translated yet never shows blank.
// Usage: getLocalized(product, 'name', lang) reads product.name / product.nameZh
// Shorthand specifically for category names, since they're displayed in
// many places across the site.
export const catName = (cat, lang) => getLocalized(cat, 'name', lang);

export const getLocalized = (obj, field, lang) => {
    if (!obj) return '';
    if (lang === 'zh') {
        const zhValue = obj[`${field}Zh`];
        if (zhValue && String(zhValue).trim()) return zhValue;
    }
    return obj[field] || '';
};

// Static UI dictionary for hardcoded text — filled in as each screen is
// tackled (Phase 2 onward). Usage: uiText('addToCart', lang)
const UI_STRINGS = {
    searchPlaceholder: { en: 'Search for products, brands and more', zh: '搜尋商品、品牌及更多內容' },
    myProfile: { en: 'My Profile', zh: '我的個人資料' },
    navOrders: { en: 'Orders', zh: '訂單' },
    navMyOrders: { en: 'My Orders', zh: '我的訂單' },
    wishlist: { en: 'Wishlist', zh: '願望清單' },
    coupons: { en: 'Coupons', zh: '優惠券' },
    logout: { en: 'Logout', zh: '登出' },
    logoutSuccess: { en: 'Logout Successfully', zh: '登出成功' },
    downloadApp: { en: 'Download App', zh: '下載應用程式' },
    navHome: { en: 'Home', zh: '首頁' },
    navShop: { en: 'Shop', zh: '商店' },
    myAccount: { en: 'My Account', zh: '我的帳戶' },
    welcomeGuest: { en: 'Welcome, Guest', zh: '歡迎，訪客' },
    loginSignUp: { en: 'Login / Sign Up', zh: '登入 / 註冊' },
    loginHeading: { en: 'Login', zh: '登入' },
    loginSubheading: { en: 'Get access to your Orders, Wishlist and Recommendations', zh: '存取您的訂單、願望清單及推薦內容' },
    emailLabel: { en: 'Email', zh: '電郵' },
    passwordLabel: { en: 'Password', zh: '密碼' },
    forgotPassword: { en: 'Forgot Password?', zh: '忘記密碼？' },
    loginWithCode: { en: 'Login with Email Code Instead', zh: '改用電郵驗證碼登入' },
    newToSite: { en: 'New to 1313 Mart? Create an account', zh: '首次使用 1313 Mart？建立帳戶' },
    agreeToTermsPrefix: { en: "By continuing, you agree to 1313 Mart's", zh: '繼續即表示您同意 1313 Mart 的' },
    termsOfUse: { en: 'Terms of Use', zh: '使用條款' },
    andWord: { en: 'and', zh: '及' },
    privacyPolicy: { en: 'Privacy Policy.', zh: '私隱政策。' },
    registerTitle: { en: "Looks like you're new here!", zh: '看來您是第一次來！' },
    registerTag: { en: 'Sign up with your mobile number to get started', zh: '使用您的手機號碼註冊以開始使用' },
    fullNameLabel: { en: 'Full Name', zh: '全名' },
    yourGender: { en: 'Your Gender :', zh: '性別：' },
    male: { en: 'Male', zh: '男' },
    female: { en: 'Female', zh: '女' },
    confirmPasswordLabel: { en: 'Confirm Password', zh: '確認密碼' },
    chooseFile: { en: 'Choose File', zh: '選擇檔案' },
    signup: { en: 'Signup', zh: '註冊' },
    existingUserLogin: { en: 'Existing User? Log in', zh: '已有帳戶？登入' },
    passwordTooShort: { en: 'Password length must be atleast 8 characters', zh: '密碼長度必須至少為8個字符' },
    passwordMismatch: { en: "Password Doesn't Match", zh: '密碼不相符' },
    selectAvatar: { en: 'Select Avatar', zh: '請選擇頭像' },
    forgotPasswordTitle: { en: 'Forgot Your Password?', zh: '忘記密碼？' },
    forgotPasswordTag: { en: 'Enter the email address associated with your account.', zh: '請輸入與您帳戶相關聯的電郵地址。' },
    forgotPasswordHeading: { en: 'Forgot Password', zh: '忘記密碼' },
    submit: { en: 'Submit', zh: '提交' },
    otpLoginTitle: { en: 'Login with a code', zh: '使用驗證碼登入' },
    otpLoginTag: { en: "No password needed — we'll email you a 6-digit code to log in.", zh: '無需密碼——我們將透過電郵發送6位數驗證碼供您登入。' },
    otpEmailSent: { en: 'If that email is registered, a code is on its way', zh: '如該電郵已註冊，驗證碼將盡快發送' },
    sendCode: { en: 'Send Code', zh: '發送驗證碼' },
    backToPasswordLogin: { en: 'Back to Password Login', zh: '返回密碼登入' },
    somethingWentWrong: { en: 'Something went wrong', zh: '發生錯誤，請稍後再試' },
    almostThere: { en: 'Almost there!', zh: '快完成了！' },
    verifyEmailTag: { en: "We've sent a 6-digit code to your email — enter it to finish setting up your account.", zh: '我們已將6位數驗證碼發送到您的電郵——請輸入以完成帳戶設定。' },
    verifyLoginTag: { en: "We've sent a 6-digit code to your email — no password needed.", zh: '我們已將6位數驗證碼發送到您的電郵——無需密碼。' },
    enterCodeSentTo: { en: 'Enter the code we sent to', zh: '請輸入我們發送到以下地址的驗證碼' },
    sixDigitCode: { en: '6-digit code', zh: '6位數驗證碼' },
    verify: { en: 'Verify', zh: '驗證' },
    resendCode: { en: 'Resend code', zh: '重新發送驗證碼' },
    resendCodeIn: { en: 'Resend code in', zh: '重新發送驗證碼於' },
    enterSixDigitCode: { en: 'Please enter the 6-digit code', zh: '請輸入6位數驗證碼' },
    emailVerified: { en: 'Email verified!', zh: '電郵已驗證！' },
    loggedIn: { en: 'Logged in!', zh: '已登入！' },
    verificationFailed: { en: 'Verification failed', zh: '驗證失敗' },
    newCodeSent: { en: 'A new code has been sent', zh: '新驗證碼已發送' },
    couldNotResend: { en: 'Could not resend code', zh: '無法重新發送驗證碼' },
    myCart: { en: 'My Cart', zh: '我的購物車' },
    placeOrder: { en: 'PLACE ORDER', zh: '下單' },
    savedForLater: { en: 'Saved For Later', zh: '稍後購買' },
    sellerLabel: { en: 'Seller:', zh: '賣家：' },
    offLabel: { en: 'off', zh: '折扣' },
    appliedLabel: { en: 'applied', zh: '已套用' },
    saveForLaterBtn: { en: 'SAVE FOR LATER', zh: '稍後購買' },
    removeBtn: { en: 'REMOVE', zh: '移除' },
    maxOrderQty: { en: 'Maximum Order Quantity', zh: '已達最大訂購數量' },
    productRemovedFromCart: { en: 'Product Removed From Cart', zh: '商品已從購物車移除' },
    savedForLaterToast: { en: 'Saved For Later', zh: '已加入稍後購買' },
    qtyLabel: { en: 'Qty:', zh: '數量：' },
    emptyCartMsg: { en: 'Your cart is empty!', zh: '您的購物車是空的！' },
    emptyCartSub: { en: 'Add items to it now.', zh: '立即加入商品。' },
    shopNow: { en: 'Shop Now', zh: '立即購物' },
    stepLogin: { en: 'LOGIN', zh: '登入' },
    stepDeliveryAddress: { en: 'DELIVERY ADDRESS', zh: '送貨地址' },
    stepOrderSummary: { en: 'ORDER SUMMARY', zh: '訂單摘要' },
    stepPaymentOptions: { en: 'PAYMENT OPTIONS', zh: '付款選項' },
    itemWord: { en: 'Item', zh: '件商品' },
    paymentWord: { en: 'Payment', zh: '付款' },
    chooseDeliveryAddress: { en: 'Choose a delivery address', zh: '選擇送貨地址' },
    defaultBadge: { en: 'Default', zh: '預設' },
    addNewAddressInstead: { en: '+ Add a new address instead', zh: '+ 改為新增地址' },
    chooseSavedAddressInstead: { en: '← Choose from saved addresses instead', zh: '← 改為選擇已儲存的地址' },
    addressFieldLabel: { en: 'Address (building, street, floor, etc.)', zh: '地址（大廈、街道、樓層等）' },
    regionLabel: { en: 'Region', zh: '地區' },
    areaLabel: { en: 'Area', zh: '分區' },
    deliveryDaysLabel: { en: 'Delivery days:', zh: '送貨日期：' },
    freeShippingNote: { en: 'Free shipping on orders over', zh: '訂單滿' },
    otherwiseShippingNote: { en: 'otherwise', zh: '否則收取' },
    shippingWord: { en: 'shipping', zh: '運費' },
    saveAddressCheckbox: { en: 'Save this address for future orders', zh: '儲存此地址以供日後訂單使用' },
    saveAndDeliverHere: { en: 'save and deliver here', zh: '儲存並送貨至此' },
    deliverHere: { en: 'deliver here', zh: '送貨至此' },
    pleaseChooseDeliveryArea: { en: 'Please choose a delivery area', zh: '請選擇送貨地區' },
    failedToSaveAddress: { en: 'Failed to save address', zh: '儲存地址失敗' },
    backToCart: { en: 'Back to Cart', zh: '返回購物車' },
    hkPhoneDigits: { en: 'Hong Kong phone numbers must be 8 digits', zh: '香港電話號碼必須為8位數字' },
    validPhoneNumber: { en: 'Please enter a valid phone number', zh: '請輸入有效的電話號碼' },
    phoneNoLabel: { en: 'Phone No', zh: '電話號碼' },
    codeLabel: { en: 'Code', zh: '區碼' },
    priceDetails: { en: 'PRICE DETAILS', zh: '價格明細' },
    enterPromoCode: { en: 'Enter promo code', zh: '輸入優惠碼' },
    apply: { en: 'Apply', zh: '套用' },
    appliedSaved: { en: 'Applied — you saved', zh: '已套用——您節省了' },
    remove: { en: 'Remove', zh: '移除' },
    deliveringTo: { en: 'Delivering to', zh: '送貨至' },
    addMoreForFreeShipping: { en: 'more for free shipping', zh: '即可享免運費' },
    qualifyFreeShipping: { en: 'You qualify for free shipping to this area', zh: '此地區可享免運費' },
    mrpLabel: { en: 'MRP', zh: '原價' },
    itemsWord: { en: 'item', zh: '件商品' },
    productDiscount: { en: 'Product Discount', zh: '商品折扣' },
    salePrice: { en: 'Sale Price', zh: '售價' },
    offerDiscountLabel: { en: 'Offer Discount', zh: '優惠折扣' },
    promoDiscountLabel: { en: 'Promo Discount', zh: '優惠碼折扣' },
    shippingChargeLabel: { en: 'Shipping Charge', zh: '運費' },
    freeLabel: { en: 'FREE', zh: '免費' },
    calculatedAtCheckout: { en: 'Calculated at checkout', zh: '結帳時計算' },
    totalAmountLabel: { en: 'Total Amount', zh: '總金額' },
    youWillSave: { en: 'You will save', zh: '您將節省' },
    onThisOrder: { en: 'on this order', zh: '於此訂單' },
    walletAppliedLabel: { en: '1313 Wallet Applied', zh: '1313 Wallet 已抵扣' },
    cashOnDelivery: { en: 'Cash on Delivery', zh: '貨到付款' },
    creditDebitCard: { en: 'Credit / Debit Card', zh: '信用卡／扣賬卡' },
    comingSoonSuffix: { en: '(Coming Soon)', zh: '（即將推出）' },
    alipayRedirectNote: { en: "You'll be redirected to Alipay to complete your payment securely, then brought back here automatically once it's done.", zh: '您將被轉至支付寶完成安全付款，完成後將自動返回此頁面。' },
    wechatPayQrNote: { en: 'A QR code will appear for you to scan with the WeChat app to complete your payment.', zh: '將顯示二維碼，請使用微信應用程式掃描以完成付款。' },
    payWithWallet: { en: 'Pay with 1313 Wallet', zh: '使用 1313 Wallet 付款' },
    loadMoney: { en: '+ Load Money', zh: '+ 增值' },
    pointsHave: { en: 'Points (have', zh: '積分（現有' },
    cashHave: { en: '1313 Cash (have', zh: '1313 Cash（現有' },
    closeParen: { en: ')', zh: '）' },
    applyingFromWallet: { en: 'Applying', zh: '正在使用' },
    fromYourWalletYoullPay: { en: "from your wallet — you'll pay", zh: '來自您的錢包——您將支付' },
    fullyCoveredNote: { en: '(fully covered, no other payment needed)', zh: '（已全額支付，無需其他付款）' },
    walletMaxNote1: { en: 'Up to', zh: '最多' },
    walletMaxNote2: { en: '% of this order (max HK$', zh: '% 的訂單金額（上限 HK$' },
    walletMaxNote3: { en: ') can be covered by your wallet.', zh: '）可由您的錢包支付。' },
    paySecurelyWithCard: { en: 'Pay securely with card', zh: '使用信用卡安全付款' },
    cardNumberLabel: { en: 'Card Number', zh: '卡號' },
    expiryDateLabel: { en: 'Expiry Date', zh: '有效期限' },
    cvvLabel: { en: 'CVV', zh: 'CVV' },
    stripeSecureNote: { en: '🔒 Payments are securely processed by Stripe. We never see or store your card details.', zh: '🔒 付款由 Stripe 安全處理。我們絕不會查看或儲存您的卡片資料。' },
    backWord: { en: 'Back', zh: '返回' },
    placeOrderAmount: { en: 'Place Order — HK$', zh: '下單 — HK$' },
    missingPhoneNote: { en: 'Your saved address is missing a phone number. Please update it before placing your order.', zh: '您儲存的地址缺少電話號碼。請在下單前更新。' },
    orderPlaced: { en: 'Order placed!', zh: '訂單已成立！' },
    paymentFormLoading: { en: 'Payment form is still loading — please try again in a moment', zh: '付款表格仍在載入中——請稍後再試' },
    paymentNotCompleted: { en: 'Payment was not completed', zh: '付款未完成' },
    cardChargedOrderFailed: { en: 'Your card was charged, but the order could not be completed:', zh: '您的卡已被扣款，但訂單未能完成：' },
    contactSupportNote: { en: 'Please contact support before trying again.', zh: '請在重試前聯絡客服。' },
    orderConfirmEmailNote: { en: 'Order confirmation email will be sent to', zh: '訂單確認電郵將發送至' },
    continueWord: { en: 'continue', zh: '繼續' },
    transactionSuccessful: { en: 'Successfull', zh: '成功' },
    transactionFailed: { en: 'Failed', zh: '失敗' },
    transactionWord: { en: 'Transaction', zh: '交易' },
    redirectingTo: { en: 'Redirecting to', zh: '正在轉至' },
    inSec: { en: 'in', zh: '，' },
    secWord: { en: 'sec', zh: '秒後' },
    ordersWord: { en: 'orders', zh: '訂單' },
    cartWord: { en: 'cart', zh: '購物車' },
    goTo: { en: 'go to', zh: '前往' },
    removedFromSaveForLater: { en: 'Removed From Save For Later', zh: '已從稍後購買移除' },
    productAddedToCart: { en: 'Product Added To Cart', zh: '商品已加入購物車' },
    moveToCartBtn: { en: 'MOVE TO CART', zh: '移至購物車' },
    filtersWord: { en: 'Filters', zh: '篩選' },
    clearAll: { en: 'clear all', zh: '清除全部' },
    orderStatusLabel: { en: 'ORDER STATUS', zh: '訂單狀態' },
    orderTimeLabel: { en: 'ORDER TIME', zh: '訂單時間' },
    thisMonth: { en: 'This Month', zh: '本月' },
    searchOrdersPlaceholder: { en: 'Search your orders here', zh: '在此搜尋您的訂單' },
    searchOrdersBtn: { en: 'Search Orders', zh: '搜尋訂單' },
    noResultsFound: { en: 'Sorry, no results found', zh: '抱歉，找不到結果' },
    editSearchOrClear: { en: 'Edit search or clear all filters', zh: '修改搜尋或清除所有篩選' },
    emptyInput: { en: 'Empty Input', zh: '請輸入內容' },
    orderedOn: { en: 'Ordered on', zh: '於' },
    orderedOnSuffix: { en: '', zh: '下單' },
    deliveredOn: { en: 'Delivered on', zh: '於' },
    deliveredOnSuffix: { en: '', zh: '送達' },
    orderNumberHash: { en: 'Order #', zh: '訂單 #' },
    andMoreItem: { en: 'and', zh: '及' },
    moreItemsWord: { en: 'more item', zh: '件其他商品' },
    itemsCountWord: { en: 'item', zh: '件商品' },
    totalLabel: { en: 'Total:', zh: '總額：' },
    driverLabel: { en: 'Driver:', zh: '司機：' },
    ptsEarned: { en: 'pts earned', zh: '積分已賺取' },
    walletUsedLabel: { en: 'Wallet used', zh: '已使用錢包' },
    deliveryAddressLabel: { en: 'Delivery Address', zh: '送貨地址' },
    emailLabelPlain: { en: 'Email', zh: '電郵' },
    phoneNumberLabel: { en: 'Phone Number', zh: '電話號碼' },
    orderStatusHeading: { en: 'Order Status', zh: '訂單狀態' },
    downloadInvoice: { en: 'Download Invoice', zh: '下載發票' },
    editOrder: { en: 'Edit Order', zh: '編輯訂單' },
    orderLockedTooltip: { en: 'This order is already packed, you cannot edit it now', zh: '此訂單已打包，現在無法編輯' },
    tableItem: { en: 'Item', zh: '商品' },
    tableQty: { en: 'Qty', zh: '數量' },
    tablePrice: { en: 'Price', zh: '單價' },
    tableTotal: { en: 'Total', zh: '總額' },
    mrpSubtotal: { en: 'MRP Subtotal', zh: '原價小計' },
    promoLabel: { en: 'Promo', zh: '優惠碼' },
    pointsUsedLabel: { en: '1313 Points Used', zh: '已使用 1313 積分' },
    ptsWord: { en: 'pts', zh: '分' },
    cashUsedLabel: { en: '1313 Cash Used', zh: '已使用 1313 Cash' },
    totalPaidLabel: { en: 'Total Paid', zh: '已付總額' },
    youEarnedEmoji: { en: '🎉 You earned', zh: '🎉 您已賺取' },
    pointsWord: { en: 'points', zh: '積分' },
    orderUpdatesHeading: { en: 'Order Updates', zh: '訂單更新' },
    failedDownloadInvoice: { en: 'Failed to download invoice', zh: '下載發票失敗' },
    orderCancelledNote: { en: 'This order has been cancelled.', zh: '此訂單已被取消。' },
    orderTrackingHeading: { en: 'Order Tracking', zh: '訂單追蹤' },
    isOnTheWay: { en: 'is on the way', zh: '正在送貨途中' },
    waitingForDriverLocation: { en: "Waiting for the driver's live location...", zh: '正在等待司機的即時位置...' },
    yourDeliveryDriver: { en: 'Your delivery driver:', zh: '您的送貨司機：' },
    removeFromWishlist: { en: 'Remove From Wishlist', zh: '已從願望清單移除' },
    addedToWishlist: { en: 'Added To Wishlist', zh: '已加入願望清單' },
    productOutOfStock: { en: 'Product is out of stock', zh: '此商品缺貨' },
    addedToCart: { en: 'Added To Cart', zh: '已加入購物車' },
    noMoreStock: { en: 'No more stock available', zh: '庫存不足' },
    outOfStock: { en: 'Out of stock', zh: '缺貨中' },
    addBtn: { en: 'Add', zh: '加入' },
    myWishlistHeading: { en: 'My Wishlist', zh: '我的願望清單' },
    emptyWishlistHeading: { en: 'Empty Wishlist', zh: '願望清單是空的' },
    emptyWishlistSub: { en: 'You have no items in your wishlist. Start adding!', zh: '您的願望清單目前沒有商品，立即開始加入吧！' },
    helloComma: { en: 'Hello,', zh: '您好，' },
    myOrdersNav: { en: 'MY ORDERS', zh: '我的訂單' },
    accountSettingsNav: { en: 'ACCOUNT SETTINGS', zh: '帳戶設定' },
    profileInformation: { en: 'Profile Information', zh: '個人資料' },
    manageAddresses: { en: 'Manage Addresses', zh: '管理地址' },
    myStuffNav: { en: 'MY STUFF', zh: '我的內容' },
    myCoupons: { en: 'My Coupons', zh: '我的優惠券' },
    myReviewsRatings: { en: 'My Reviews & Ratings', zh: '我的評論與評分' },
    myWishlistNav: { en: 'My Wishlist', zh: '我的願望清單' },
    frequentlyVisited: { en: 'Frequently Visited:', zh: '常用連結：' },
    changePassword: { en: 'Change Password', zh: '更改密碼' },
    trackOrder: { en: 'Track Order', zh: '追蹤訂單' },
    helpCenter: { en: 'Help Center', zh: '幫助中心' },
    priceLabel: { en: 'PRICE', zh: '價格' },
    toWord: { en: 'to', zh: '至' },
    categoryLabel: { en: 'Category', zh: '分類' },
    searchCategoriesPlaceholder: { en: 'Search categories...', zh: '搜尋分類...' },
    noCategoriesMatch: { en: 'No categories match', zh: '沒有符合的分類' },
    brandLabel: { en: 'Brand', zh: '品牌' },
    searchBrandsPlaceholder: { en: 'Search brands...', zh: '搜尋品牌...' },
    noBrandsMatch: { en: 'No brands match', zh: '沒有符合的品牌' },
    noResultsExclaim: { en: 'Sorry, no results found!', zh: '抱歉，找不到結果！' },
    checkSpellingNote: { en: 'Please check the spelling or try searching for something else', zh: '請檢查拼字或嘗試搜尋其他內容' },
    inStock: { en: 'IN STOCK', zh: '有庫存' },
    outOfStockCaps: { en: 'OUT OF STOCK', zh: '缺貨' },
    outOfStockBtn: { en: 'Out of Stock', zh: '缺貨' },
    addToCartLower: { en: 'Add to cart', zh: '加入購物車' },
    emptyReview: { en: 'Empty Review', zh: '評論不可為空' },
    reviewSubmitted: { en: 'Review Submitted Successfully', zh: '評論已成功提交' },
    goToCartBtn: { en: 'GO TO CART', zh: '前往購物車' },
    addToCartBtn: { en: 'ADD TO CART', zh: '加入購物車' },
    buyNowBtn: { en: 'BUY NOW', zh: '立即購買' },
    reviewsWord: { en: 'Reviews', zh: '則評論' },
    specialPrice: { en: 'Special Price', zh: '特價' },
    availableOffers: { en: 'Available offers', zh: '可用優惠' },
    variationsLabel: { en: 'Variations', zh: '款式選擇' },
    brandLabelPlain: { en: 'Brand', zh: '品牌' },
    descriptionLabel: { en: 'Description', zh: '產品描述' },
    productDescriptionHeading: { en: 'Product Description', zh: '產品描述' },
    specificationsHeading: { en: 'Specifications', zh: '規格' },
    noAdditionalInfo: { en: 'No additional information provided.', zh: '未提供其他資訊。' },
    ratingsReviewsHeading: { en: 'Ratings & Reviews', zh: '評分與評論' },
    rateProductBtn: { en: 'Rate Product', zh: '評價此商品' },
    submitReviewTitle: { en: 'Submit Review', zh: '提交評論' },
    reviewLabel: { en: 'Review', zh: '評論' },
    cancelBtn: { en: 'Cancel', zh: '取消' },
    submitBtn: { en: 'Submit', zh: '提交' },
    byWord: { en: 'by', zh: '由' },
    viewLess: { en: 'View Less', zh: '收起' },
    viewAll: { en: 'View All', zh: '查看全部' },
    similarProducts: { en: 'Similar Products', zh: '相似商品' },
    fromSameCategory: { en: 'From the same category', zh: '來自同一分類' },
    moreFrom: { en: 'More from', zh: '更多來自' },
    youMightAlsoLike: { en: 'You might also like', zh: '您可能也喜歡' },
    trendingProducts: { en: 'Trending Products', zh: '熱門商品' },
    sendUsMessage: { en: 'Send Us a Message', zh: '傳送訊息給我們' },
    yourNamePlaceholder: { en: 'Your Name', zh: '您的姓名' },
    yourEmailPlaceholder: { en: 'Your Email', zh: '您的電郵' },
    phoneOptionalPlaceholder: { en: 'Phone Number (optional)', zh: '電話號碼（選填）' },
    yourMessagePlaceholder: { en: 'Your Message', zh: '您的訊息' },
    sendingText: { en: 'Sending...', zh: '傳送中...' },
    sendMessageBtn: { en: 'Send Message', zh: '傳送訊息' },
    getInTouch: { en: 'Get in Touch', zh: '聯絡我們' },
    getDirections: { en: '📍 Get Directions', zh: '📍 取得路線指引' },
    brandsWeDistribute: { en: 'Brands We Distribute', zh: '我們代理的品牌' },
    ourStores: { en: 'Our Stores', zh: '我們的分店' },
};

// Order status values are fixed system enums (not admin-editable), so
// they're translated as a static lookup rather than a per-record field.
const ORDER_STATUS_ZH = {
    Processing: '處理中',
    Accepted: '已接受',
    Packed: '已打包',
    Shipped: '已發貨',
    'Out for Delivery': '派送中',
    Delivered: '已送達',
    Cancelled: '已取消',
};

export const translateStatus = (status, lang) => {
    if (lang === 'zh' && ORDER_STATUS_ZH[status]) return ORDER_STATUS_ZH[status];
    return status;
};

const ORDER_CARD_SUB_ZH = {
    Processing: '賣家已處理您的訂單',
    Accepted: '您的訂單已被接受',
    Packed: '您的訂單已打包',
    Shipped: '您的訂單已發貨',
    Delivered: '您的訂單已送達',
    Cancelled: '此訂單已被取消',
};
const ORDER_CARD_SUB_EN = {
    Processing: 'Seller has processed your order',
    Accepted: 'Your order has been accepted',
    Packed: 'Your order has been packed',
    Shipped: 'Your order has been Shipped',
    Delivered: 'Your order has been Delivered',
    Cancelled: 'This order has been cancelled',
};
export const orderCardSubtext = (status, lang) => (lang === 'zh' ? ORDER_CARD_SUB_ZH[status] : ORDER_CARD_SUB_EN[status]) || '';

export const uiText = (key, lang) => {
    const entry = UI_STRINGS[key];
    if (!entry) return key; // visible fallback so a missing key is obvious, not blank
    return entry[lang] || entry.en || key;
};